import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import type { AppearanceColorThemeSettingItemValue } from '../constants/internalToolsAppSettings.ts'

/**
 * Apply theme to the whole HTML document page.
 * If `theme: undefined`, it uses light theme by default.
 *
 * The implementation focuses on whether to enable dark theme,
 * so the light theme is implicitly the default theme.
 *
 * When `theme: 'dark'`, it always applies dark theme.
 * When `theme: 'system'`, the theme dynamically changes
 * based on the system dark mode preference in real-time.
 */
export const useApplyPageTheme = (
  theme: AppearanceColorThemeSettingItemValue | undefined,
) => {
  const systemDarkModeMediaQuery = useMemo(() => {
    return window.matchMedia('(prefers-color-scheme: dark)')
  }, [])

  const applyTheme = useCallback(() => {
    const shouldApplyDarkTheme =
      // When the theme is dark
      theme === 'dark' ||
      // When the theme is system, and the prefer color is dark
      (theme === 'system' && systemDarkModeMediaQuery.matches)

    document.documentElement.classList.toggle('dark', shouldApplyDarkTheme)
  }, [systemDarkModeMediaQuery.matches, theme])

  // Apply the theme immediately on mount.
  useLayoutEffect(applyTheme, [applyTheme])

  // Apply the theme when the system dark mode preference changes.
  useEffect(() => {
    systemDarkModeMediaQuery.addEventListener('change', applyTheme)
    return () => {
      systemDarkModeMediaQuery.removeEventListener('change', applyTheme)
    }
  }, [applyTheme, systemDarkModeMediaQuery])
}
